/** leads/ScanControl.tsx — 증자 자동 스캔 시작/진행률/중지 (벤처기업 탭 툴바용) */
import React, { useEffect, useRef, useState } from 'react';
import { scanStart, scanStatus, scanStop } from './api';
import type { ScanStatus } from './types';

interface Props { region?: string; sigungu?: string; selectedIds?: string[]; onToast: (m: string, t?: string) => void; onFinished: () => void }

export default function ScanControl({ region, sigungu, selectedIds, onToast, onFinished }: Props) {
  const [st, setSt] = useState<ScanStatus | null>(null);
  const timer = useRef<number | null>(null);
  const wasRunning = useRef(false);

  const poll = async () => {
    try {
      const s = await scanStatus();
      setSt(s);
      if (s.running) { wasRunning.current = true; timer.current = window.setTimeout(poll, 3000); }
      else if (wasRunning.current) {
        wasRunning.current = false;
        onToast(s.pausedByQuota ? '일일 쿼터 도달 — 내일 이어서 스캔하세요 (스캔 ' + s.scanned + '건)' :
          (s.mode === 'crno' ? '조회 완료 — ' + s.scanned + '건 중 법인번호 확보 ' + s.crnoFound + '건 · 미확인 ' + s.noCrno + '건 · 동명검토 ' + s.ambiguous + '건' : '스캔 완료 — ' + s.scanned + '건 중 증자 ' + s.increases + '건 · 리드 ' + s.leads + '건' + (s.outPeriod ? ' · 기간외 축적 ' + s.outPeriod + '건' : '')));
        onFinished();
      }
    } catch (e) { /* 폴링 실패는 조용히 재시도 */ timer.current = window.setTimeout(poll, 5000); }
  };
  useEffect(() => { poll(); return () => { if (timer.current) window.clearTimeout(timer.current); }; }, []); // eslint-disable-line

  const CONFIRM: Record<string, string> = {
    crno: '의 법인등록번호만 조회합니다.\n(개요 API 1콜/사 — 재무 쿼터 미소비, 업체명 핵심어+지역+대표자 성 교차검증)',
    fs: '의 증자이력을 조회합니다.\n(법인번호 보유 업체만 · 2021년 이후 증자 → 증자 업체 탭, 유효기간 겹침 → 경정청구 리드 자동)',
    full: '를 법인번호부터 증자이력까지 한 번에 조회합니다.\n(개요 + 재무 — 두 쿼터 모두 소비)',
  };
  const begin = async (mode: 'crno' | 'fs' | 'full') => {
    const nSel = selectedIds?.length || 0;
    const scopeLabel = nSel > 0 ? '선택한 ' + nSel + '개사' : (region || '전국') + (sigungu ? ' ' + sigungu : '');
    if (!window.confirm(scopeLabel + CONFIRM[mode] + (nSel > 0 ? '\n(선택 건은 기조회분도 재조회)' : '') + '\n시작할까요?')) return;
    try { setSt(await scanStart(region, sigungu, nSel > 0 ? selectedIds : undefined, mode)); wasRunning.current = true; timer.current = window.setTimeout(poll, 2000); }
    catch (e) { onToast((e as Error).message, 'error'); }
  };

  if (st?.running) {
    const pct = st.total ? Math.round((st.scanned / st.total) * 100) : 0;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#4B5563' }}>
        <span>🔎 스캔 {st.scanned.toLocaleString()}/{st.total.toLocaleString()} ({pct}%) · 증자 {st.increases} · 리드 {st.leads}{st.lastCorp ? ' · ' + st.lastCorp : ''}</span>
        <button className="ld-btn" style={{ padding: '5px 10px', fontSize: 12, color: '#DC2626' }} onClick={async () => setSt(await scanStop())}>중지</button>
      </span>
    );
  }
  const OUTCOME: Record<string, [string, string]> = {
    done: ['완료', '#16A34A'], quota: ['일일 쿼터 도달 — 다음 실행 때 이어서 진행', '#D97706'],
    stopped: ['수동 중지됨', '#6B7280'], error: ['연속 오류로 중단 — 로그 확인 필요', '#DC2626'],
    interrupted: ['서버 재시작으로 중단 — 다음 실행 때 이어서 진행', '#D97706'], running: ['실행 중', '#2563EB'],
  };



  const lr = st?.lastRun;
  const [outLabel, outColor] = lr ? (OUTCOME[lr.outcome] || [lr.outcome, '#6B7280']) : ['', ''];
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <button className="ld-btn" onClick={() => begin('crno')} title="① 개요 API만 — 법인번호 선확보 (핵심어+지역+대표자 성 교차검증)">법인번호만 조회{selectedIds && selectedIds.length > 0 ? ' (선택 ' + selectedIds.length + '건)' : ''}</button>
      <button className="ld-btn ok" onClick={() => begin('fs')} title="② 법인번호 보유 업체만 — 2021년 이후 증자 전부 원장 등록, 유효기간 겹침은 리드 자동">증자이력 조회{selectedIds && selectedIds.length > 0 ? ' (선택 ' + selectedIds.length + '건)' : ''}</button>
      <button className="ld-btn" onClick={() => begin('full')} title="③ ①+② 일괄 — 두 쿼터 모두 소비">법인번호+증자이력{selectedIds && selectedIds.length > 0 ? ' (선택 ' + selectedIds.length + '건)' : ''}</button>
      {lr && (
        <span style={{ fontSize: 11.5, color: '#6B7280' }}>
          지난 실행 {new Date(lr.started_at).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          {lr.trigger === 'nightly' ? ' (야간)' : ''} · <b style={{ color: outColor }}>{outLabel}</b>
          {lr.stats && typeof lr.stats === 'object' && 'scanned' in lr.stats ? (String(lr.trigger).endsWith(':crno')
            ? ' · 조회 ' + (lr.stats as { scanned: number }).scanned + '건, 확보 ' + ((lr.stats as { crno_found?: number }).crno_found ?? 0) + '건, 미확인 ' + ((lr.stats as { noCrno?: number }).noCrno ?? 0) + '건, 동명 ' + ((lr.stats as { ambiguous?: number }).ambiguous ?? 0) + '건'
            : ' · 스캔 ' + (lr.stats as { scanned: number }).scanned + '건, 리드 ' + ((lr.stats as { leads?: number }).leads ?? 0) + '건') : ''}
          {st && st.remaining > 0 ? ' · 대기 ' + st.remaining.toLocaleString() + '곳' : ' · 대기 없음'}
        </span>
      )}
    </span>
  );
}
